import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import { syncWriteOutcome } from "shared/reservationSyncData"
import { ReservationSyncDto } from "src/features/things/reservationSyncApi"
import { fmtNum, PartLoader } from "./ReservationSyncChrome"
import { useReservationSyncReport } from "./useReservationSyncReport"

/** A sync ledger row as the script stored it. */
type SyncWriteDto = {
  sstId: string
  sl2ReservationId: string
  applied: boolean
  error?: string
  changes: { field: string; from?: string; to?: string }[]
}

/** A revert ledger row. */
type RevertWriteDto = {
  sstId: string
  reservationId: string
  outcome: "reverted" | "refused" | "skipped" | "failed"
  reason?: string
  changes?: { field: string; from: unknown; to: unknown }[]
}

/**
 * Map a stored sync write onto the vocabulary this table shares with the revert
 * ledger, using the SAME classifier the publisher counted with — so a row's label
 * can never contradict the totals above it. "applied" is rendered as the success
 * state, which the revert calls "reverted".
 */
const outcomeOf = (w: SyncWriteDto): RevertWriteDto["outcome"] => {
  const o = syncWriteOutcome({
    applied: w.applied,
    error: w.error,
    changeCount: w.changes.length,
  })
  return o === "applied" ? "reverted" : o
}

const CHIP: Record<string, "success" | "warning" | "error" | "default"> = {
  reverted: "success",
  refused: "warning",
  failed: "error",
  skipped: "default",
}

/**
 * One component for BOTH write ledgers, because they answer the same question in
 * the same shape — which rows were written, which were refused, and why. The only
 * differences are the vocabulary and which direction the arrow points, so they are
 * parameters rather than a second copy of the table.
 */
export const WriteLedgerPanel: FC<{
  run: ReservationSyncDto
  kind: "sync" | "revert"
}> = ({ run, kind }) => {
  const { reportOf, loadNextPart } = useReservationSyncReport<
    SyncWriteDto | RevertWriteDto
  >(run)
  const report = reportOf(kind)
  const [filter, setFilter] = useState<string>("all")
  const counts = (kind === "sync" ? run.syncCounts : run.revertCounts) ?? {}
  const hasObjects = run.objects.some((o) => o.kind === kind)

  if (!hasObjects)
    return (
      <Box sx={{ p: 4, textAlign: "center", border: 1, borderColor: "divider", borderRadius: 1 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          {kind === "sync"
            ? "No sync has been applied to this locker"
            : "Nothing has been reverted"}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 620, mx: "auto" }}>
          {kind === "sync"
            ? "This run was an assessment: it read both sides, stored the drift report, and stopped. Nothing in our data changed, so there is no before-and-after to show. Running the sync with --apply advances this same run rather than creating a new one."
            : "The sync on this run still stands. revertReservationSync.ts reads this run's before-snapshots and writes each applied row back, then advances this record to sync reverted."}
        </Typography>
      </Box>
    )

  const rows = report.rows.filter((r) => {
    if (filter === "all") return true
    const o = "outcome" in r ? r.outcome : outcomeOf(r as SyncWriteDto)
    return o === filter
  })

  return (
    <Box data-ui="UI-SL2W-165">
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {Object.entries(counts).map(([k, v]) => (
          <Chip
            key={k}
            size="small"
            color={CHIP[k === "applied" ? "reverted" : k] ?? "default"}
            variant={v ? "filled" : "outlined"}
            label={`${k} ${fmtNum(v)}`}
          />
        ))}
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        {kind === "sync"
          ? "Authority is the partner: every write copies the SST value onto our row, and the partner is never written to. A refused row is the interesting one — a guard stopped a patch that would otherwise have been silently stripped while reporting success."
          : "A revert only touches rows this sync actually wrote, and only where the row still equals the after-snapshot the sync recorded. Anything else is refused rather than overwritten, so a revert can never discard a later write."}
      </Alert>

      <PartLoader
        report={report}
        onLoadMore={() => loadNextPart(kind)}
        label={kind === "sync" ? "Write ledger" : "Revert ledger"}
      />
      {!report.partsLoaded && !report.loading && (
        <Button size="small" variant="outlined" onClick={() => loadNextPart(kind)}>
          Load the {kind} ledger
        </Button>
      )}

      {report.partsLoaded > 0 && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ my: 2 }}>
            {["all", "applied", "reverted", "refused", "skipped", "failed"]
              .filter((k) => k === "all" || counts[k] !== undefined)
              .map((k) => (
                <Chip
                  key={k}
                  size="small"
                  label={k}
                  onClick={() => setFilter(k === "applied" ? "reverted" : k)}
                  variant={
                    filter === (k === "applied" ? "reverted" : k) ? "filled" : "outlined"
                  }
                />
              ))}
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SST id</TableCell>
                <TableCell>Our reservation id</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>{kind === "sync" ? "Field written" : "Restored"}</TableCell>
                <TableCell>Why not</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, 400).map((r) => {
                const id = "reservationId" in r ? r.reservationId : r.sl2ReservationId
                const outcome = "outcome" in r ? r.outcome : outcomeOf(r as SyncWriteDto)
                const wrote = outcome === "reverted"
                const reason = "reason" in r ? r.reason : (r as SyncWriteDto).error
                return (
                  <TableRow key={`${r.sstId}-${id}`} hover>
                    <TableCell sx={{ fontFamily: "monospace" }}>{r.sstId}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>{id}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={CHIP[outcome] ?? "default"}
                        label={
                          kind === "sync" && outcome === "reverted" ? "applied" : outcome
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {(r.changes ?? []).map((c: any) => (
                        <Box key={c.field} sx={{ fontSize: "0.72rem" }}>
                          <Box component="span" sx={{ color: "text.disabled" }}>
                            {c.field}{" "}
                          </Box>
                          <Box
                            component="span"
                            sx={{ fontFamily: "monospace", textDecoration: "line-through" }}
                          >
                            {String(c.from ?? "absent")}
                          </Box>
                          {" → "}
                          <Box
                            component="span"
                            sx={{
                              fontFamily: "monospace",
                              ...(wrote ? { color: "success.main", fontWeight: 600 } : {}),
                            }}
                          >
                            {String(c.to ?? "absent")}
                          </Box>
                        </Box>
                      ))}
                      {!wrote && (
                        <Typography variant="caption" sx={{ color: "text.disabled" }}>
                          left as it was
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.72rem", maxWidth: 320 }}>
                      {reason ?? "—"}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          {rows.length > 400 && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              showing the first 400 of {fmtNum(rows.length)} loaded row(s)
            </Typography>
          )}
        </>
      )}
    </Box>
  )
}
